// Platform-host detection for branding + super-admin console routing.
//
// The Tynvara PLATFORM owns a SMALL, EXPLICIT set of hosts. EVERY other host is a
// TENANT host (e.g. dev.blanchette-employees.com, echelon-employees.com) whose brand
// must be resolved via GET /api/identity/branding.
//
// These MUST be exact-match allowlists — NOT loose prefixes like startsWith('dev.') or
// startsWith('admin.'). Loose prefixes are what leaked the Tynvara platform brand onto
// dev.<tenant>.com hosts after the dev.* subdomain migration, and would likewise hijack
// any tenant custom domain that happened to start with "admin.". Keep these lists in sync
// with the backend PLATFORM_HOSTS set (identity/backend/routers/auth.py).

// Hosts that render the platform's own Tynvara brand + normal portal.
const PLATFORM_BRAND_HOSTS = new Set([
  'tynvara.com',
  'www.tynvara.com',
  'dev.tynvara.com',
])

// Hosts that render the super-admin (system_admin) console instead of the tenant portal.
// admin.dev.tynvara.com is the dev counterpart of admin.tynvara.com (see dev.* migration).
const ADMIN_CONSOLE_HOSTS = new Set([
  'admin.tynvara.com',
  'admin.dev.tynvara.com',
])

const normalize = (host) => (host || '').toLowerCase()

export function isAdminHost(host = window.location.hostname) {
  return ADMIN_CONSOLE_HOSTS.has(normalize(host))
}

export function isPlatformBrandHost(host = window.location.hostname) {
  return PLATFORM_BRAND_HOSTS.has(normalize(host))
}

// Staging tenants on their OWN domains (the dev.* subdomain migration). Exact names: a company's own
// domain beginning with "dev." proves nothing - a production company may use one.
const STAGING_TENANT_HOSTS = new Set([
  'dev.blanchette-employees.com',
  'dev.echelon-employees.com',
])

/**
 * Which environment this browser is looking at, for a label a platform admin can never misread
 * (Super Admin header). ONE bundle serves staging and production (promote.sh builds the same VITE_*
 * values for both), and no backend route says which it is, so the HOST decides - but only for hosts
 * KNOWN to be one or the other (review SA-3):
 *
 *   'local'      - localhost, 127.0.0.1, [::1], *.localhost, *.test
 *   'staging'    - dev.tynvara.com, anything under .dev.tynvara.com (admin.dev, staging.dev, a tenant's
 *                  subdomain), and the staging tenants' own domains listed above
 *   'production' - tynvara.com and anything else under .tynvara.com
 *   'unknown'    - every other host: a company's own domain says nothing about which environment
 *                  serves it, so no claim is made. Calling production "staging" is the dangerous
 *                  mistake, and a guess is how it happens (a loose startsWith('dev.') did exactly that).
 *
 * A LABEL ONLY: nothing may be allowed or refused on it.
 */
export function platformEnvironment(host = window.location.hostname) {
  const h = normalize(host)
  if (!h || h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1'
      || h.endsWith('.localhost') || h.endsWith('.test')) return 'local'
  if (h === 'dev.tynvara.com' || h.endsWith('.dev.tynvara.com') || STAGING_TENANT_HOSTS.has(h)) return 'staging'
  if (h === 'tynvara.com' || h.endsWith('.tynvara.com')) return 'production'
  return 'unknown'
}
